import axios from "axios";
import JSONBig from "json-bigint";
import { UIC_AUTH_KEYS, getUserId } from "./auth";

// 大数字解析器：超过 Number.MAX_SAFE_INTEGER 的数字自动转为字符串，避免精度丢失
const JSONBigString = JSONBig({ storeAsString: true });

// 创建axios实例
const service = axios.create({
  baseURL: "/api", // 基础路径，需与后端接口或代理配置匹配
  timeout: 200000, // 超时时间
  headers: {
    "Content-Type": "application/json",
    // 这里不预设Authorization，避免被拦截器覆盖
  },
  transformResponse: [
    (data) => {
      try {
        return JSONBigString.parse(data);
      } catch {
        return data;
      }
    },
  ],
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 优先从存储中获取token（登录后场景）
    const token =
      sessionStorage.getItem(UIC_AUTH_KEYS.accessToken) ||
      localStorage.getItem(UIC_AUTH_KEYS.accessToken);
    const tokenType =
      sessionStorage.getItem(UIC_AUTH_KEYS.tokenType) ||
      localStorage.getItem(UIC_AUTH_KEYS.tokenType) ||
      "Bearer";

    if (token) {
      // 已登录状态：使用后端要求的Uic-Auth头
      config.headers["Uic-Auth"] = `${tokenType} ${token}`;
    } else {
      // 未登录状态：使用接口要求的Basic Auth
      // 注意：修复原代码中的Base64编码字符串错误（少了字符）
      config.headers["Authorization"] = "Basic aGVmZW5nOmhlZmVuZ19zZWNyZXQ=";
    }

    // 固定添加租户ID，与接口要求一致
    config.headers["Tenant-Id"] = "000001";

    // 添加用户ID信息到请求头
    const userId = getUserId();
    if (userId) {
      config.headers["User-Id"] = userId;
      config.headers["X-User-Id"] = userId; // 同时添加常用的X-User-Id头
    } else {
      console.warn("未获取到用户ID");
      // 在登录页面初始化时已尝试从URL获取用户ID，这里直接使用getUserId即可
    }

    return config;
  },
  (error) => {
    console.error("请求拦截器错误:", error);
    return Promise.reject(error);
  },
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 统一处理接口返回格式
    const res = response.data;

    console.log("接口返回数据:", res);

    // 处理"暂无承载数据"的情况，需要重新登录
    // 可通过 config._skipNoDataRedirect 跳过此检查（某些接口正常返回空数据）
    if (
      !response.config._skipNoDataRedirect &&
      res.code === 200 &&
      res.success &&
      res.data === null &&
      res.msg === "暂无承载数据"
    ) {
      console.log("检测到登录过期，准备跳转到登录页面:", res);
      // 清除无效的token信息
      localStorage.removeItem(UIC_AUTH_KEYS.accessToken);
      localStorage.removeItem(UIC_AUTH_KEYS.tokenType);
      localStorage.removeItem(UIC_AUTH_KEYS.userInfo);
      sessionStorage.removeItem(UIC_AUTH_KEYS.accessToken);
      sessionStorage.removeItem(UIC_AUTH_KEYS.tokenType);
      sessionStorage.removeItem(UIC_AUTH_KEYS.userInfo);

      // 直接跳转到登录页面，不做路径判断
      console.log("执行跳转登录页面");
      window.location.href = "/login";
      return Promise.reject(new Error(res.msg || "登录已过期，请重新登录"));
    }

    // 根据后端实际返回格式调整判断逻辑
    if (res.code !== 200 && !res.success) {
      // 业务错误：直接抛出错误信息
      return Promise.reject(new Error(res.msg || "接口返回异常"));
    }

    return res;
  },
  (error) => {
    console.error("响应拦截器错误:", error);

    // 处理401未授权
    if (error.response && error.response.status === 401) {
      // 清除无效的token信息
      localStorage.removeItem(UIC_AUTH_KEYS.accessToken);
      localStorage.removeItem(UIC_AUTH_KEYS.tokenType);
      localStorage.removeItem(UIC_AUTH_KEYS.userInfo);

      // 如果当前不在登录页，跳转到登录页
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    // 提取错误信息
    let errorMsg = "网络请求失败，请稍后重试";
    if (error.response?.data?.msg) {
      errorMsg = error.response.data.msg;
    } else if (error.response?.data?.error_description) {
      errorMsg = error.response.data.error_description;
    } else if (error.message) {
      errorMsg = error.message;
    }

    return Promise.reject(new Error(errorMsg));
  },
);

export default service;
